import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import {
  FaDoorOpen,
  FaSyncAlt,
  FaTruck,
  FaHome,
  FaArrowUp,
  FaArrowDown,
  FaTimesCircle,
  FaSave,
} from "react-icons/fa";
import styles from "./EmptyGateOut.module.css";

// Same key helper as Test Certificate / Re-Weigh / Stale screens
const getPNumber = (row) => row?.P_NUMBER ?? row?.p_number ?? row?.P_Number ?? "";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#38bdf8" : "#d8e4f0",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(14,165,233,0.14)" : "none",
    fontSize: "0.94rem",
  }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: "12px", overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.9rem",
    fontWeight: state.isSelected ? 800 : 500,
  }),
};

const EmptyGateOut = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;
  const navigate = useNavigate();

  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const [vehicles, setVehicles] = useState([]);
  const [selectedPNumber, setSelectedPNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [popup, setPopup] = useState({ open: false, title: "", message: "" });

  const showPopup = (message, title = "Notice") =>
    setPopup({ open: true, title, message });
  const closePopup = () => setPopup({ open: false, title: "", message: "" });

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.post("/api/wbauto/emptyGateOut/list", {
        emp_id,
        token,
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setVehicles(rows);
      setSelectedPNumber("");

      if (rows.length === 0) {
        setToast("No eligible vehicles found.");
      }
    } catch (err) {
      setVehicles([]);
      showPopup(
        err?.response?.data?.message || "Failed to load vehicles.",
        "Error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const vehicleOptions = useMemo(
    () =>
      vehicles
        .filter((r) => getPNumber(r))
        .map((r) => ({
          value: String(getPNumber(r)),
          label: `${r.P_TRUCK || "-"} — ${r.P_DOCNUMBER || "-"} — ${r.P_Item_name || "-"}`,
          row: r,
        })),
    [vehicles]
  );

  const selected = useMemo(
    () =>
      vehicles.find((r) => String(getPNumber(r)) === String(selectedPNumber)) ||
      null,
    [vehicles, selectedPNumber]
  );

  const handleCancel = () => setSelectedPNumber("");

  const handleSubmit = async () => {
    if (!selectedPNumber) {
      showPopup("Select a vehicle first.", "Validation");
      return;
    }

    const firstWt = selected?.P_FIRSTWT;
    if (firstWt == null || firstWt === "") {
      showPopup("First weight is missing for this vehicle.", "Validation");
      return;
    }

    const toNum = (v) => {
      if (v == null || String(v).trim() === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const netWt = toNum(selected?.P_NETWT);
    const tolerance = toNum(
      selected?.WBM_Tolerance_val ?? selected?.wbm_tolerance_val
    );

    if (netWt != null && tolerance != null && netWt >= tolerance) {
      showPopup("This is beyond tolerance, Contact IT.", "Blocked");
      return;
    }

    const ok = window.confirm(
      `Set Second Weight = First Weight (${firstWt}) and Net Weight = 0 for ${
        selected?.P_TRUCK || "this vehicle"
      }?`
    );
    if (!ok) return;

    setSaving(true);
    try {
      const res = await axios.post("/api/wbauto/emptyGateOut/submit", {
        emp_id,
        token,
        pNumber: Number(selectedPNumber),
      });

      if (res.data?.success) {
        setToast(`✅ ${res.data.message}`);
        await fetchList();
      } else {
        showPopup(res.data?.message || "Update failed.", "Error");
      }
    } catch (err) {
      showPopup(err?.response?.data?.message || "Update failed.", "Error");
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.loginBox}>
          <FaTruck className={styles.loginIcon} />
          <p>You must be logged in.</p>
          <button className={styles.primaryBtn} onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div ref={topRef} />

      {toast && <div className={styles.toast}>{toast}</div>}

      {popup.open && (
        <div className={styles.modalBackdrop} onClick={closePopup}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h4>{popup.title}</h4>
            <p>{popup.message}</p>
            <button className={styles.primaryBtn} onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      <div className={styles.hero}>
        <div className={styles.heroIcon}>
          <FaDoorOpen />
        </div>
        <div>
          <h2>Empty Gate Out</h2>
          <p>Select a vehicle, to release empty gate out.</p>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.sectionTitle}>Select Vehicle</div>

        <Select
          styles={selectStyles}
          isSearchable
          isClearable
          isDisabled={loading || saving}
          placeholder={loading ? "Loading..." : "Search Vehicle / Doc / Item"}
          value={vehicleOptions.find((o) => o.value === selectedPNumber) || null}
          options={vehicleOptions}
          filterOption={(option, input) => {
            const q = String(input || "").toUpperCase();
            const r = option.data.row;
            return (
              String(r.P_TRUCK || "").toUpperCase().includes(q) ||
              String(r.P_DOCNUMBER || "").toUpperCase().includes(q) ||
              String(r.P_Item_name || "").toUpperCase().includes(q) ||
              String(r.P_Weigh_Type || "").toUpperCase().includes(q)
            );
          }}
          onChange={(opt) => setSelectedPNumber(opt?.value || "")}
        />

        <div className={styles.topButtons}>
          <button
            className={styles.refreshBtn}
            onClick={fetchList}
            disabled={loading || saving}
          >
            <FaSyncAlt /> {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button
            className={styles.cancelBtn}
            onClick={() => navigate("/dashboard")}
            disabled={saving}
          >
            <FaTimesCircle /> Exit
          </button>
        </div>

        <div className={styles.resultMeta}>{vehicles.length} vehicles loaded</div>
      </div>

      {selected && (
        <div className={styles.panel}>
          <div className={styles.sectionTitle}>Vehicle Details</div>
          <div className={styles.formGrid}>
            <Info label="Vehicle No" value={selected.P_TRUCK} />
            <Info label="Doc Number" value={selected.P_DOCNUMBER} />
            <Info label="Item Name" value={selected.P_Item_name} />

            <div className={styles.formRow2}>
              <Info label="First WB No" value={selected.P_FIRSTWBNO} />
              <Info label="Second WB No" value={selected.P_SECONDWBNO} />
            </div>

            <Info label="First Weight" value={selected.P_FIRSTWT} />
            <Info label="Second Weight" value={selected.P_SECONDWT} />
            <Info label="Net Weight" value={selected.P_NETWT} />
            {/* <Info
              label="Tolerance"
              value={selected.WBM_Tolerance_val ?? selected.wbm_tolerance_val}
            /> */}
          </div>

          <div className={styles.actionPanel}>
            <button
              className={styles.submitBtn}
              onClick={handleSubmit}
              disabled={saving}
            >
              <FaSave /> {saving ? "Saving..." : "Submit"}
            </button>
            <button
              className={styles.cancelBtn}
              onClick={handleCancel}
              disabled={saving}
            >
              <FaTimesCircle /> Cancel
            </button>
          </div>
        </div>
      )}

      {!selected && !loading && (
        <div className={styles.emptyBox}>
          Select a vehicle to view Empty Gate Out details.
        </div>
      )}

      <div className={styles.fabStack}>
        <button
          className={styles.fabBtn}
          onClick={() => topRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          <FaArrowUp />
        </button>
        <button
          className={styles.fabBtn}
          onClick={() => bottomRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          <FaArrowDown />
        </button>
        <button
          className={`${styles.fabBtn} ${styles.homeFab}`}
          onClick={() => navigate("/dashboard")}
        >
          <FaHome />
        </button>
      </div>

      <div ref={bottomRef} />
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className={styles.formItem}>
    <label>{label}</label>
    <div className={styles.infoValue}>{value ?? "-"}</div>
  </div>
);

export default EmptyGateOut;
